import React from "react";
import { Box, Typography, Button, Avatar, useTheme } from "@mui/material";
import { Edit } from "@mui/icons-material";
import { cloudinaryService, numprice } from "@/helpers";
import { PreorderCartItemProps } from "./PreorderCartItem.types";
import { QuantityControl } from "../QuantityControl";

const PreorderCartItem: React.FC<PreorderCartItemProps> = ({
  item,
  onQuantityChange,
  onEditItem,
  disabled = false,
}) => {
  const theme = useTheme();
  const formatPrice = (price: number) => `${price / 100} ₽`; // Convert from cents to rubles

  // State for optimized image
  const [optimizedImage, setOptimizedImage] = React.useState<{
    url: string;
    isCloudinary: boolean;
    fallbackUsed: boolean;
  } | null>(null);

  const [imageLoading, setImageLoading] = React.useState<boolean>(false);

  const productImage = item.dish.images?.[0]?.url || null;

  // Effect to optimize dish image
  React.useEffect(() => {
    if (!productImage) {
      setOptimizedImage(null);
      setImageLoading(false);
      return;
    }

    let isCancelled = false;
    setImageLoading(true);

    cloudinaryService
      .smartTransformImage(productImage, {
        width: 50,
        height: 50,
        crop: "fill",
        gravity: "auto",
        quality: "auto",
        format: "auto",
      })
      .then((result) => {
        if (isCancelled) return;

        setOptimizedImage({
          url: result.url,
          isCloudinary: result.isCloudinary,
          fallbackUsed: result.fallbackUsed,
        });

        if (result.fallbackUsed) {
          console.info(
            "PreorderCartItem: Using fallback URL for dish:",
            item.dish.name
          );
        }
      })
      .catch((error) => {
        if (isCancelled) return;

        console.error("PreorderCartItem: Image optimization failed:", error);
        setOptimizedImage({
          url: productImage,
          isCloudinary: false,
          fallbackUsed: true,
        });
      })
      .finally(() => {
        if (!isCancelled) {
          setImageLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [productImage, item.dish.name]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        paddingBottom: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Product Image */}
      <Avatar
        src={optimizedImage?.url || productImage || ""}
        alt={item.dish.name}
        variant="rounded"
        sx={{
          width: 50,
          height: 50,
          borderRadius: "10px",
          opacity: imageLoading ? 0.7 : 1,
          transition: theme.transitions.create("opacity", {
            duration: theme.transitions.duration.short,
          }),
        }}
      />

      {/* Product Details */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          minWidth: 0,
        }}
      >
        {/* Name and Description */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              color: theme.palette.text.primary,
              lineHeight: 1.1,
              marginBottom: 0.5,
            }}
          >
            {item.dish.name}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: "13px",
              color: theme.palette.text.primary,
              opacity: 0.7,
              lineHeight: 1.1,
              marginBottom:
                item.modifiers && item.modifiers.length > 0 ? 0.5 : 0,
            }}
          >
            {item.dish.description || ""}
          </Typography>

          {/* Modifications */}
          {item.modifiers &&
            item.modifiers.map((modifier, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  fontSize: "13px",
                  color: theme.palette.text.primary,
                  opacity: 0.7,
                  lineHeight: 1.1,
                }}
              >
                – {modifier.name}
              </Typography>
            ))}

          {/* Edit Button */}
          {/* <Button
            onClick={onEditItem}
            disabled={disabled}
            startIcon={<Edit sx={{ fontSize: 14 }} />}
            sx={{
              marginTop: 0.5,
              padding: 0,
              minWidth: "auto",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              color: theme.palette.text.primary,
              lineHeight: 1.4,
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Изменить
          </Button> */}
        </Box>
      </Box>

      {/* Price and Quantity */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        {/* Price */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            fontSize: 16,
            textAlign: "right",
          }}
        >
          {numprice(item.price * item.qty)} ₽
        </Typography>

        {/* Quantity Control */}
        <QuantityControl
          quantity={item.qty}
          onQuantityChange={onQuantityChange}
          disabled={disabled}
          size="small"
          aria-label={`Quantity for ${item.dish.name}`}
        />
      </Box>
    </Box>
  );
};

export default PreorderCartItem;
